import { editTemplate } from 'api/operations/documentsService';
import { makeAutoObservable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { Document } from 'types/documentsService';
import { uploadFiles } from 'global/utils/uploadFiles';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';

interface StaticDataFormFields extends DefaultFormFields {
  templateLink: FormControl<string>;
}

interface EditTemplateContextualProps {
  document: Document;
  onSubmit: () => void;
}

export class EditTemplateModalStore {
  private readonly _document: Document;
  public form: FormController<StaticDataFormFields>;
  public uploadedFile: AcceptedFiles | null = null;
  public shouldResetUploadedFile = false;

  private _modalDelegate: ModalDelegate = new ModalDelegate();

  // On submission callback function
  private readonly _onSubmit;

  public get isVisible(): boolean {
    return this._modalDelegate.isVisible;
  }

  public setShouldResetUploadedFile(value: boolean): void {
    this.shouldResetUploadedFile = value;
  }

  public get documentName(): string {
    return this._document.type || '';
  }

  public setFile(file: AcceptedFiles | null): void {
    this.uploadedFile = file;
  }

  public openEditModal(): void {
    this._modalDelegate.open();
    this.setShouldResetUploadedFile(false);
  }

  public closeEditModal(): void {
    this._modalDelegate.close();
    this.resetModal();
  }

  public resetModal(): void {
    this.form.resetForm();
    this.setFile(null);
    this.setShouldResetUploadedFile(true);
  }

  private async _requestDocumentAction(): Promise<void> {
    await this._processDocumentAction();
    runInAction(() => {
      this.resetModal();
      this._onSubmit();
    });
  }

  private async _processDocumentAction(): Promise<void> {
    const fileURL = await editTemplate({
      documentId: this._document.id,
      templateLink: this.uploadedFile ? '' : this.form.fields.templateLink.value.trim(),
      fileName: this.uploadedFile ? Object.keys(this.uploadedFile)[0] : '',
    });
    await runInAction(async () => {
      if (this.uploadedFile && fileURL) {
        const uploadedFileName = Object.keys(this.uploadedFile)[0];
        await uploadFiles(this.uploadedFile, { [uploadedFileName]: fileURL });
      }

      runInAction(() => {
        this.closeEditModal();
      });
    });
  }

  constructor(props: EditTemplateContextualProps) {
    this._document = props.document;
    this._onSubmit = props.onSubmit;

    this.form = new FormController<StaticDataFormFields>(
      {
        templateLink: new FormControl<string>(this._document.templateLink || '', {
          validations: ['validUrl'],
          optional: true,
        }),
      },
      { onSubmit: this._requestDocumentAction.bind(this) },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
